import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Icons } from "../Icons";
import { Button } from "../Button/Button";
import { PopupContext } from './PopupContext';
import {
    createLayerId,
    pushLayer,
    popLayer,
    isTopLayer,
    getLayerZIndex,
    addScrollLock,
    removeScrollLock,
} from './popupStack';

const FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Popup — The main modal/dialog component of the design system.
 *
 * Features:
 * - Portal render into document.body (escapes overflow clipping).
 * - Stack-based z-index via popupStack — popup-over-popup works correctly.
 * - Scroll lock with reference counting — multiple popups don't fight.
 * - Focus trap (Tab/Shift+Tab stays within the dialog).
 * - Escape closes only the topmost layer.
 * - Focus is saved on open and restored on close.
 * - Exposes PopupContext with { close, zIndex } for nested components.
 *
 * @param {string}        name               - Title displayed in the header
 * @param {string}        ariaLabel          - Accessible dialog name when the visual title is a React node
 * @param {string}        titleIcon          - Optional icon displayed before the title
 * @param {string}        subtitle           - Optional supporting text below the title
 * @param {string}        customClassName     - Optional class on the dialog container
 * @param {func}          handleOnClosePopup - Callback for close icon, backdrop, and Escape
 * @param {number|string} maxWidth           - Maximum width of the container (default 480)
 * @param {boolean}       show               - Toggle visibility
 * @param {node}          children           - Content for the scrollable body
 * @param {object}        primaryAction      - { label, onClick, isLoading, disabled, Type }
 * @param {object}        secondaryAction    - { label, onClick, disabled, Type }
 * @param {object}        extraAction        - { label, onClick, disabled } e.g. "Clear filters"
 * @param {node}          footerContent      - Custom footer content (overrides standard actions)
 * @param {boolean}       showHeaderDivider  - Toggle header divider line
 * @param {boolean}       showFooterDivider  - Toggle footer divider line
 * @param {object}        customContentStyle - Custom style for the content area
 * @param {string}        extraActionClassName - Class name for the extra action
 */
export const Popup = ({
    name,
    ariaLabel,
    titleIcon,
    subtitle,
    handleOnClosePopup,
    maxWidth = 480,
    customPopupStyle = {},
    show = true,
    children,
    primaryAction,
    secondaryAction,
    tertiaryAction,
    extraAction,
    footerContent,
    showHeaderDivider = true,
    showFooterDivider = true,
    customContentStyle = {},
    extraActionClassName = "body-s-bold",
    customHeaderStyle = {},
    autoFocusFirst = false,
    customClassName = "",
}) => {
    const [shouldRender, setShouldRender] = useState(show);
    const [isExiting, setIsExiting] = useState(false);

    // Stable ID for this popup instance across re-renders
    const layerIdRef = useRef(createLayerId());
    // Captured z-index — set once when popup opens, stays stable through exit animation
    const [zIndex, setZIndex] = useState(null);
    // Container ref for focus trap
    const containerRef = useRef(null);
    // Overlay closes only when the press started on it — a drag (popup or textarea resize, text selection) released outside must not close
    const pressStartedOnOverlayRef = useRef(false);
    // Element focused before popup opened — restored on close
    const prevFocusRef = useRef(null);

    useEffect(() => {
        if (show) {
            setShouldRender(true);
            setIsExiting(false);
            pushLayer(layerIdRef.current);
            addScrollLock(layerIdRef.current);
            setZIndex(getLayerZIndex(layerIdRef.current));
            prevFocusRef.current = document.activeElement;
            // Defer focus so the container is in the DOM
            requestAnimationFrame(() => {
                if (containerRef.current) {
                    if (autoFocusFirst) {
                        const first = containerRef.current.querySelector(FOCUSABLE_SELECTORS);
                        (first || containerRef.current).focus();
                    } else {
                        containerRef.current.focus();
                    }
                }
            });
        } else if (shouldRender) {
            setIsExiting(true);
            popLayer(layerIdRef.current);
            removeScrollLock(layerIdRef.current);
            prevFocusRef.current?.focus();
        }
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    // Safety cleanup if the component is unmounted without show going false first
    useEffect(() => {
        const id = layerIdRef.current;
        return () => {
            popLayer(id);
            removeScrollLock(id);
        };
    }, []);

    const handleKeyDown = useCallback((e) => {
        // Escape — only the topmost layer responds
        if (e.key === 'Escape') {
            if (isTopLayer(layerIdRef.current)) {
                e.stopPropagation();
                handleOnClosePopup?.();
            }
            return;
        }

        // Focus trap — keep Tab cycling within the dialog
        if (e.key === 'Tab' && containerRef.current) {
            const focusable = Array.from(containerRef.current.querySelectorAll(FOCUSABLE_SELECTORS));
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        }
    }, [handleOnClosePopup]);

    const contextValue = React.useMemo(() => ({ close: handleOnClosePopup, zIndex }), [handleOnClosePopup, zIndex]);

    if (!shouldRender) return null;

    const handleAnimationEnd = (e) => {
        // Only respond to animations on the container itself, not child elements
        if (e.target === containerRef.current && isExiting) setShouldRender(false);
    };

    return ReactDOM.createPortal(
        <PopupContext.Provider value={contextValue}>
            <div
                className={`popup_overlay ${isExiting ? 'popup_overlay--exiting' : ''}`}
                style={{ zIndex: zIndex }}
                onMouseDown={(e) => { pressStartedOnOverlayRef.current = e.target === e.currentTarget }}
                onClick={(e) => pressStartedOnOverlayRef.current && e.target === e.currentTarget && handleOnClosePopup?.()}
                onKeyDown={handleKeyDown}
            >
                <div
                    ref={containerRef}
                    className={`popup_container ${customClassName} ${isExiting ? 'popup_container--exiting' : ''}`}
                    style={{ maxWidth, ...customPopupStyle }}
                    onAnimationEnd={handleAnimationEnd}
                    tabIndex={-1}
                    role="dialog"
                    aria-modal="true"
                    aria-label={ariaLabel || (typeof name === "string" ? name : undefined)}
                >
                    {/* ─── Header ─── */}
                    {name && (
                        <div className={`popup_header ${!showHeaderDivider ? 'no_divider' : ''}`} style={customHeaderStyle}>
                            <div className="popup_heading">
                                <div className="popup_title body-l-bold">
                                    {titleIcon && (
                                        <span className="popup_title_icon">
                                            <Icons name={titleIcon} size={16} />
                                        </span>
                                    )}
                                    <span>{name}</span>
                                </div>
                                {subtitle && <div className="popup_subtitle body-s-semibold">{subtitle}</div>}
                            </div>
                            {handleOnClosePopup && (
                                <div className="close_button" onClick={handleOnClosePopup}>
                                    <Icons name="close" size={16} />
                                </div>
                            )}
                        </div>
                    )}

                    {/* ─── Body Content ─── */}
                    <div className="popup_content" style={customContentStyle}>
                        {children}
                    </div>

                    {/* ─── Footer Actions ─── */}
                    {(primaryAction || secondaryAction || tertiaryAction || extraAction || footerContent) && (
                        <div className={`popup_footer ${!showFooterDivider ? 'no_divider' : ''}`}>
                            {footerContent ? (
                                footerContent
                            ) : (
                                <>
                                    <div className="popup_footer_left">
                                        {extraAction && (
                                            <div
                                                className={`extra_action_btn ${extraAction.disabled ? 'disabled' : ''} ${extraAction.className || ''}`}
                                                onClick={extraAction.disabled ? undefined : extraAction.onClick}
                                                style={extraAction.style || {}}
                                            >
                                                <span className={extraActionClassName}>{extraAction.label}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="popup_footer_right">
                                        {tertiaryAction && (
                                            <Button
                                                Type={tertiaryAction.Type || "Tertiary"}
                                                Size={tertiaryAction.Size || "Medium"}
                                                onClick={tertiaryAction.onClick}
                                                disabled={tertiaryAction.disabled}
                                                {...tertiaryAction}
                                            >
                                                {tertiaryAction.label}
                                            </Button>
                                        )}
                                        {secondaryAction && (
                                            <Button
                                                Type={secondaryAction.Type || "Secondary"}
                                                Size={secondaryAction.Size || "Medium"}
                                                onClick={secondaryAction.onClick}
                                                disabled={secondaryAction.disabled}
                                                {...secondaryAction}
                                            >
                                                {secondaryAction.label}
                                            </Button>
                                        )}
                                        {primaryAction && (
                                            <Button
                                                Type={primaryAction.Type || "Primary"}
                                                Size={primaryAction.Size || "Medium"}
                                                onClick={primaryAction.onClick}
                                                isLoading={primaryAction.isLoading}
                                                disabled={primaryAction.disabled}
                                                {...primaryAction}
                                            >
                                                {primaryAction.label}
                                            </Button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </PopupContext.Provider>,
        document.body
    );
};
